import { Controller } from 'react-hook-form';
import { Select, SelectSize, SelectType, Textarea } from '@ui/src';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';
import AdditionalInfoBlock from '../additional-info-block';
import { CustomerStatus } from '../../../../hoc/customers/types';
import { SourceOfFundsTypes } from './types';

import cn from '../aml.module.css';

const SourceOfFunds = ({
  control,
  customerSourceOfFunds,
  clearErrors,
  fields,
  setValue,
  status,
  errors,
}: SourceOfFundsTypes) => (
  <AdditionalInfoBlock title="Source of funds">
    <RegulatedCompanyRow title="Source of funds to be credited to the account">
      <Controller
        control={control}
        name="sourcesOfFundsIds"
        defaultValue={[]}
        render={({ field: { onChange, value, name } }) => (
          <Select
            name={name}
            placeholder="Select an option..."
            list={
              customerSourceOfFunds?.map((item) => ({
                name: item.value,
                value: item.id,
              })) || []
            }
            value={value}
            size={SelectSize.SMALL}
            onChange={(e) => {
              onChange(e);
              clearErrors(['sourcesOfFundsIds']);

              if (
                !(e.target.value as string[])?.includes('Other') &&
                fields?.sourcesOfFundsOther
              ) {
                clearErrors(['sourcesOfFundsOther']);
                setValue('sourcesOfFundsOther', '');
              }

              if (
                !(e.target.value as string[])?.includes('Other') &&
                fields?.sourcesOfFundsLoanCreditInstitution
              ) {
                clearErrors(['sourcesOfFundsLoanCreditInstitution']);
                setValue('sourcesOfFundsLoanCreditInstitution', '');
              }

              if (
                !(e.target.value as string[])?.includes('Other') &&
                fields?.sourcesOfFundsLoanBusinessPartner
              ) {
                clearErrors(['sourcesOfFundsLoanBusinessPartner']);
                setValue('sourcesOfFundsLoanBusinessPartner', '');
              }
            }}
            selectWrapperClassName={
              status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
            }
            disabled={status === CustomerStatus.CLOSED}
            tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            error={errors?.sourcesOfFundsIds?.message}
            mode={SelectType.MULTIPLE}
            fullWidth
            borderless
          />
        )}
      />
    </RegulatedCompanyRow>
    {fields?.sourcesOfFundsIds?.find((item) =>
      customerSourceOfFunds
        ?.find((client) => item === client.id)
        ?.value?.includes('Other'),
    ) && (
      <RegulatedCompanyRow title="Provide details" textarea>
        <Controller
          control={control}
          name="sourcesOfFundsOther"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Textarea
              name={name}
              title=""
              placeholder="Enter details..."
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['sourcesOfFundsOther']);
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              error={errors?.sourcesOfFundsOther?.message}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        />
      </RegulatedCompanyRow>
    )}
    {fields?.sourcesOfFundsIds?.find((item) =>
      customerSourceOfFunds
        ?.find((client) => item === client.id)
        ?.value?.includes('Loan from a credit institution'),
    ) && (
      <RegulatedCompanyRow
        title="Credit institution which have provided loan"
        textarea
      >
        <Controller
          control={control}
          name="sourcesOfFundsLoanCreditInstitution"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Textarea
              name={name}
              placeholder="Enter details..."
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['sourcesOfFundsLoanCreditInstitution']);
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.sourcesOfFundsLoanCreditInstitution?.message}
            />
          )}
        />
      </RegulatedCompanyRow>
    )}
    {fields?.sourcesOfFundsIds?.find((item) =>
      customerSourceOfFunds
        ?.find((client) => item === client.id)
        ?.value?.includes('Loan from a business partner'),
    ) && (
      <RegulatedCompanyRow
        title="Business partner which provided loan"
        textarea
      >
        <Controller
          control={control}
          name="sourcesOfFundsLoanBusinessPartner"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Textarea
              name={name}
              placeholder="Enter details..."
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['sourcesOfFundsLoanBusinessPartner']);
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.sourcesOfFundsLoanCreditInstitution?.message}
            />
          )}
        />
      </RegulatedCompanyRow>
    )}
    <RegulatedCompanyRow title="Does the Customer have accounts in other financial institutions?">
      <Controller
        control={control}
        name="hasAccountsInOtherFinancialInstitutions"
        defaultValue=""
        render={({ field: { onChange, value, name } }) => (
          <Select
            name={name}
            placeholder="Select an option..."
            list={[
              {
                name: 'Yes',
                value: 'yes',
              },
              {
                name: 'No',
                value: 'no',
              },
            ]}
            value={value as string}
            size={SelectSize.SMALL}
            onChange={(e) => {
              onChange(e);
              clearErrors(['hasAccountsInOtherFinancialInstitutions']);

              if (
                e.target.value === 'no' &&
                fields?.otherFinancialInstitutions
              ) {
                clearErrors(['otherFinancialInstitutions']);
                setValue('otherFinancialInstitutions', '');
              }
            }}
            selectWrapperClassName={
              status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
            }
            disabled={status === CustomerStatus.CLOSED}
            tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            error={errors?.hasAccountsInOtherFinancialInstitutions?.message}
            fullWidth
            borderless
          />
        )}
      />
    </RegulatedCompanyRow>
    {fields?.hasAccountsInOtherFinancialInstitutions === 'yes' && (
      <RegulatedCompanyRow
        title="Financial institutions at which customer has accounts"
        textarea
      >
        <Controller
          control={control}
          name="otherFinancialInstitutions"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Textarea
              name={name}
              title=""
              placeholder="Enter details..."
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['otherFinancialInstitutions']);
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              error={errors?.otherFinancialInstitutions?.message}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        />
      </RegulatedCompanyRow>
    )}
  </AdditionalInfoBlock>
);

export default SourceOfFunds;
